/**
 * Automatic free-gift handling for the cart drawer.
 *
 * The <cart-free-gift> element is rendered by snippets/cart-milestones.liquid
 * and re-rendered with the drawer on every cart update, so all money maths
 * stays server-side in Liquid. This script only reads the resulting decision
 * and executes it.
 *
 * The gift line carries a `_free_gift` property. The leading underscore keeps
 * it hidden from the cart, checkout and order confirmation, and gives Liquid a
 * reliable way to exclude the line from the qualifying total — so the gift can
 * never keep itself in the cart if the automatic discount fails to apply.
 *
 * Declining: removing the gift sets the `_gift_declined` cart attribute, which
 * stops it being re-added on the next cart update. The flag is cleared as soon
 * as the cart drops back below the threshold, so a customer who later
 * re-qualifies is offered the gift again.
 */
(function () {
  const MAX_CONSECUTIVE_ACTIONS = 3;
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const DECLINE_ATTRIBUTE = '_gift_declined';

  // Module-level so the guard survives the element being destroyed and
  // recreated by each drawer re-render.
  let inFlight = false;
  let actionCount = 0;
  let lastSignature = '';

  function post(url, payload) {
    return fetch(url, { ...fetchConfig(), body: JSON.stringify(payload) }).then(
      (response) => {
        if (!response.ok) {
          throw new Error(`${url} failed (HTTP ${response.status})`);
        }
        return response.json();
      }
    );
  }

  function applySections(sections) {
    // On the cart page the theme's own cart-update handler force-reloads.
    // Reload directly rather than racing it.
    if (document.querySelector('cart-items')) {
      window.location.reload();
      return;
    }

    if (!sections) return;

    if (sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(
        sections['cart-drawer'],
        'text/html'
      );
      const target = document.querySelector('#CartDrawer .drawer-inner');
      const source = doc.querySelector('#CartDrawer .drawer-inner');
      if (target && source) target.innerHTML = source.innerHTML;
    }

    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const doc = new DOMParser().parseFromString(
          sections['cart-icon-bubble'],
          'text/html'
        );
        // Mirrors the target/source pair the theme uses in cart.js.
        const target = bubble.querySelector('.shopify-section') || bubble;
        const source = doc.querySelector('.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
      }
    }

    if (typeof AOS !== 'undefined') AOS.init();

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-free-gift' });
  }

  class CartFreeGift extends HTMLElement {
    connectedCallback() {
      // Liquid re-renders this element on every cart update, which makes
      // connectedCallback our per-update evaluation hook.
      this.evaluate();
    }

    get config() {
      return {
        enabled: this.dataset.enabled === 'true',
        variantId: this.dataset.variantId,
        threshold: parseInt(this.dataset.threshold, 10),
        qualifying: parseInt(this.dataset.qualifying, 10),
        inCart: this.dataset.inCart === 'true',
        declined: this.dataset.declined === 'true',
        lineKey: this.dataset.lineKey || '',
      };
    }

    evaluate() {
      if (inFlight) return;

      const cfg = this.config;
      if (!cfg.enabled || !cfg.variantId) return;
      if (Number.isNaN(cfg.threshold) || Number.isNaN(cfg.qualifying)) return;

      const qualifies = cfg.qualifying >= cfg.threshold;

      if (cfg.declined) {
        // Once the cart falls below the threshold the decline is forgotten, so
        // the gift is offered again if they build the cart back up.
        if (!qualifies) {
          inFlight = true;
          post(routes.cart_update_url, {
            attributes: { [DECLINE_ATTRIBUTE]: '' },
          })
            .catch((error) => console.error('[cart-free-gift]', error))
            .finally(() => {
              inFlight = false;
            });
        }
        return;
      }

      if (qualifies === cfg.inCart) {
        // Steady state. Clear the guard so a later genuine change is allowed.
        actionCount = 0;
        lastSignature = '';
        return;
      }

      if (!qualifies && !cfg.lineKey) return;

      // Guard against a mutation that never changes the rendered state
      // (e.g. the gift silently fails to add) turning into a request loop.
      const signature = [
        qualifies ? 'add' : 'remove',
        cfg.variantId,
        cfg.qualifying,
      ].join(':');

      if (signature === lastSignature) {
        actionCount += 1;
      } else {
        lastSignature = signature;
        actionCount = 1;
      }

      if (actionCount > MAX_CONSECUTIVE_ACTIONS) {
        console.warn(
          '[cart-free-gift] Giving up after repeated identical attempts:',
          signature
        );
        return;
      }

      inFlight = true;

      const request = qualifies ? this.addGift(cfg) : this.removeGift(cfg);

      request
        .then((state) => applySections(state && state.sections))
        .catch((error) => {
          // Most likely the gift variant is out of stock or unpublished.
          console.error('[cart-free-gift]', error);
        })
        .finally(() => {
          inFlight = false;

          // The refreshed markup may itself warrant an action. Re-check once;
          // the counter above stops this from becoming a loop.
          const fresh = document.querySelector('cart-free-gift');
          if (fresh && fresh !== this) fresh.evaluate();
        });
    }

    addGift(cfg) {
      return post(routes.cart_add_url, {
        items: [
          {
            id: Number(cfg.variantId),
            quantity: 1,
            properties: { _free_gift: 'true' },
          },
        ],
        sections: SECTIONS,
        sections_url: window.location.pathname,
      });
    }

    removeGift(cfg) {
      return post(routes.cart_change_url, {
        id: cfg.lineKey,
        quantity: 0,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      });
    }
  }

  if (!customElements.get('cart-free-gift')) {
    customElements.define('cart-free-gift', CartFreeGift);
  }

  // Delegated because the drawer replaces its own markup on every update, so a
  // listener bound directly to the button would not survive a re-render.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.cart-gift-remove');
    if (!button || inFlight) return;

    event.preventDefault();

    const lineKey = button.dataset.lineKey;
    if (!lineKey) return;

    inFlight = true;
    button.setAttribute('disabled', 'disabled');

    // Set the flag before removing the line, so the drawer that comes back
    // already knows not to re-add the gift.
    post(routes.cart_update_url, { attributes: { [DECLINE_ATTRIBUTE]: 'true' } })
      .then(() =>
        post(routes.cart_change_url, {
          id: lineKey,
          quantity: 0,
          sections: SECTIONS,
          sections_url: window.location.pathname,
        })
      )
      .then((state) => applySections(state && state.sections))
      .catch((error) => {
        console.error('[cart-free-gift]', error);
        button.removeAttribute('disabled');
      })
      .finally(() => {
        inFlight = false;
      });
  });
})();
